import * as React from 'react'

import { ChevronDown, ChevronUp, TriangleRight } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import { NOOP } from '~/client/src/shared/utils/noop'

import { iconProps, ISetUpNavigationRowProps } from './commonConstants'
import SetUpNavigationSubStep from './SetUpNavigationRowSubStep'

const DIV_ID_PREFIX = 'step_'
const POPOVER_CLASS_NAME = 'project-setup-popover'

@observer
export default class SetUpNavigationRow extends React.Component<ISetUpNavigationRowProps> {
  @observable private isOpen = false
  @observable private isPopoverOpen: boolean = false

  public constructor(props: ISetUpNavigationRowProps) {
    super(props)
    makeObservable(this)
  }

  @action.bound
  private onPopoverOpenChange(isOpen: boolean) {
    if (isOpen && !this.props.isShortVersion) {
      return
    }
    this.isPopoverOpen = isOpen
  }

  @action.bound
  private closePopover() {
    this.isPopoverOpen = false
  }

  private onPopoverOpenChangeComplete = (isOpen: boolean) => {
    if (isOpen) {
      this.autoScrollPopoverIntoView()
    }
  }

  public render() {
    const { step, isShortVersion, projectSetUpPageStore } = this.props
    const { substeps, getTitle, Icon: StepIcon } = step
    const current =
      this.isAnySubstepActive() && (isShortVersion || !this.isOpen)
    const titleTextElement = (
      <>
        <div className="title-text">{getTitle()}</div>
        {!isShortVersion && (
          <div className="tw:flex tw:items-center no-grow w10 tw:justify-center navigation-row-title mr18">
            {this.isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        )}
      </>
    )

    return (
      <>
        <div
          id={`${DIV_ID_PREFIX}${projectSetUpPageStore.getKeyForStep(step)}`}
          onClick={!isShortVersion ? this.handleClickSectionHeader : NOOP}
          className={classList({
            'relative navigation-row col y-center py8 ml7 brada4 hoverable-page-bg pointer': true,
            'current current-page-bg': current || this.isPopoverOpen,
            'default-cursor':
              current && (this.isPopoverOpen || !isShortVersion),
            'square h44': isShortVersion,
          })}
        >
          <Popover
            open={this.isPopoverOpen}
            onOpenChange={this.onPopoverOpenChange}
            onOpenChangeComplete={this.onPopoverOpenChangeComplete}
          >
            <Tooltip disabled={!isShortVersion}>
              <PopoverTrigger
                nativeButton={false}
                render={
                  <TooltipTrigger
                    render={<span className="navigation-row-title row" />}
                  />
                }
              >
                <div
                  className={classList({
                    'no-grow': true,
                    w32: isShortVersion,
                    px8: !isShortVersion,
                  })}
                >
                  {StepIcon && <StepIcon {...iconProps} />}
                </div>
                {titleTextElement}
              </PopoverTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {titleTextElement}
              </TooltipContent>
            </Tooltip>
            <PopoverContent
              side="right"
              align="start"
              sideOffset={10}
              alignOffset={-8}
              className={`${POPOVER_CLASS_NAME} project-setup-leftbar setup-substeps-flyout`}
            >
              <div className="navigation-group">
                <div className="navigation-group-header title row x-between">
                  {getTitle()}
                </div>
                {substeps.map(substep => (
                  <SetUpNavigationSubStep
                    getTitle={substep.getTitle}
                    key={substep.page}
                    step={substep}
                    projectSetUpPageStore={projectSetUpPageStore}
                    isShortVersion={isShortVersion}
                    onClick={this.closePopover}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {isShortVersion && (
            <TriangleRight
              size={4}
              className="tw:absolute tw:bottom-0 tw:right-1 tw:fill-current tw:text-white"
            />
          )}
        </div>
        {this.isOpen &&
          !isShortVersion &&
          substeps.map(substep => (
            <SetUpNavigationSubStep
              getTitle={substep.getTitle}
              key={substep.page}
              step={substep}
              projectSetUpPageStore={projectSetUpPageStore}
            />
          ))}
      </>
    )
  }

  private isAnySubstepActive(): boolean {
    const { step, projectSetUpPageStore } = this.props
    return step.substeps.some(
      substep => projectSetUpPageStore.currentPage === substep.page,
    )
  }

  @action.bound
  private handleClickSectionHeader() {
    const { isShortVersion, step, projectSetUpPageStore } = this.props

    // When in short version, only toggle to expand
    if (!isShortVersion || !this.isOpen) {
      this.isOpen = !this.isOpen
    }

    // When expanding, ensure the panel is in view
    if (this.isOpen) {
      requestAnimationFrame(() => {
        projectSetUpPageStore.setSidebarCollapsed(false)

        requestAnimationFrame(() => {
          const element = document.querySelector(
            `#${DIV_ID_PREFIX}${projectSetUpPageStore.getKeyForStep(step)}`,
          )
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
          }
        })
      })
    }
  }

  // Scroll the Sidebar container so the entire popover stays within viewport.
  private autoScrollPopoverIntoView() {
    if (!this.props.isShortVersion) return

    const popover = document.querySelector(
      `.${POPOVER_CLASS_NAME}`,
    ) as HTMLElement | null
    if (!popover) return

    const rect = popover.getBoundingClientRect()
    const viewportH = window.innerHeight
    const padding = 8
    let neededScroll = 0

    if (rect.bottom > viewportH - padding) {
      neededScroll = rect.bottom - (viewportH - padding)
    } else if (rect.top < padding) {
      neededScroll = rect.top - padding
    }
    if (neededScroll === 0) return

    const scrollContainer =
      (document.getElementById(
        'project-setup-leftbar-scrollable',
      ) as HTMLElement) ||
      (document.querySelector('#project-setup-pages-panel') as HTMLElement) ||
      (document.scrollingElement as HTMLElement) ||
      document.documentElement

    // If overflowing bottom we scroll down (positive). If above top we scroll up (negative value)
    try {
      scrollContainer.scrollBy({ top: neededScroll, behavior: 'smooth' })
    } catch {
      scrollContainer.scrollTop += neededScroll
    }
  }
}
